import { useQuery } from "@tanstack/react-query";
import { Loader2, QrCode } from "lucide-react";
import { tableDetails } from "../../../../services/tableService";

const TableDetails = ({ tableId }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["tableDetails", tableId],
    queryFn: () => tableDetails({ id: tableId }),
    enabled: !!tableId,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-center space-y-4">
          <Loader2 className="animate-spin text-orange-500 mx-auto" size={40} />
          <p className="text-gray-500 font-medium">Loading table details...</p>
        </div>
      </div>
    );
  }

  if (isError || !data?.data) {
    return (
      <div className="text-center py-20">
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 inline-block">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h3 className="text-red-700 font-semibold text-lg mb-2">Failed to Load Table</h3>
          <p className="text-red-600 text-sm">Unable to fetch table details. Please try again.</p>
        </div>
      </div>
    );
  }

  const table = data?.data[0];

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-green-700 p-2 rounded-lg">
            <QrCode className="text-white" size={24} />
          </div>
          <h2 className="text-2xl font-bold text-gray-800">QR Code Scanner</h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-xl shadow-md border border-green-200 flex items-center justify-center">
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 rounded-xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity"></div>
              <img src={table?.qrImageUrl} alt="QR Code" className="relative w-64 h-64 object-contain rounded-xl shadow-lg border-4 border-white" />
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-xl p-4 shadow-md border border-blue-100">
              <div className="flex items-center gap-2 text-green-600 mb-2">
                <i className="fa-solid fa-utensils text-lg"></i>
                <span className="font-semibold text-gray-700">Table Name</span>
              </div>
              <p className="text-xl font-bold text-gray-900 capitalize">{table?.tableName}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-md border border-blue-100">
              <div className="flex items-center gap-2 text-blue-600 mb-2">
                <i className="fa-solid fa-table-cells text-lg"></i>
                <span className="font-semibold text-gray-700">Table Number</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{table?.tableNumber}</p>
            </div>

            <div className="bg-white rounded-xl p-4 shadow-md border border-blue-100">
              <div className="flex items-center gap-2 text-purple-600 mb-2">
                <i className="fa-solid fa-store text-lg"></i>
                <span className="font-semibold text-gray-700">Restaurant</span>
              </div>
              <p className="text-xl font-bold text-gray-900 capitalize">{table?.qrData?.Restaurant}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-xl font-bold mb-2">Download QR Code</h3>
            <p className="text-white/90">Save the QR code image for printing or sharing</p>
          </div>
          <button
            onClick={async () => {
              try {
                const response = await fetch(table?.qrImageUrl);
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `table-${table?.tableNumber}-${table.tableName.replace(/\s+/g, '-')}-qr.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
              } catch (error) {
                console.error('Download failed:', error);
                window.open(table?.qrImageUrl, '_blank');
              }
            }}
            className="bg-white text-indigo-600 px-6 py-3 rounded-xl font-semibold hover:bg-indigo-50 transition-colors flex items-center gap-2 shadow-lg cursor-pointer"
          >
            <i className="fa-solid fa-download"></i>
            Download QR
          </button>
        </div>
      </div>
    </div>
  );
};

export default TableDetails;
